import { useEffect, useState } from "react";
import SumsubWebSdk from "@sumsub/websdk-react";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { getAccessToken } from "@/api/samsub";
import { LuLoader } from "react-icons/lu";
import * as Sentry from "@sentry/browser";
import {
  ErrorReason,
  MessagePayload,
  ReviewAnswer,
  ReviewStatus,
  SumsubEvent,
  SumsubResult,
  ErrorData,
  SumsubBlockAnswer,
} from "@/pages/quiz/base/questions/sumsubAttachmentBlock/types";
import {
  config,
  options,
} from "@/pages/quiz/base/questions/sumsubAttachmentBlock/common";
import { getErrorMessage } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/errorMessages";
import { SwitchStateLink } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/switchStateLink";
import QuestionContentDisplay from "@/pages/quiz/base/components/progressHeader/components/QuestionContentDisplay";
import { Button, ButtonType } from "@/components/common/button";

interface SumsubStateProps extends QuizQuestionProps<SumsubBlockAnswer> {
  onUploadSsn: () => void;
  hideSsnSwitch?: boolean;
}

const SumsubState = (props: SumsubStateProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [errorData, setErrorData] = useState<ErrorData>();

  const { setQuestionAnswer, uploadUserId, onUploadSsn, hideSsnSwitch } = props;

  const getAccessTokenHandler = async () => {
    setIsLoading(true);

    if (uploadUserId) {
      try {
        const result = (await getAccessToken(uploadUserId)) as SumsubResult;

        if (result?.token) {
          setAccessToken(result.token);
        }

        if (result?.verified) {
          setQuestionAnswer({ passed: true, method: "sumsub" }, true, true);
        }
      } catch (error) {
        Sentry.captureException(error);
        setErrorData({
          code: "Failed to get access token. Please try again.",
          reason: ErrorReason.NO_TOKEN,
          error: getErrorMessage(ErrorReason.NO_TOKEN),
        });

        console.error("Error getting access token:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const messageHandler = (type: string, payload: MessagePayload) => {
    if (
      type === SumsubEvent.OnApplicantStatusChanged &&
      payload.reviewStatus === ReviewStatus.Completed &&
      payload.reviewResult?.reviewAnswer === ReviewAnswer.GREEN
    ) {
      console.log("payload", payload);
      setQuestionAnswer({ passed: true, method: "sumsub" }, false, true);
    }
  };

  const errorHandler = (errorDataObject: ErrorData) => {
    setErrorData({
      code: errorDataObject.code,
      reason: errorDataObject.reason,
      error: getErrorMessage(errorDataObject.reason),
    });
    Sentry.captureException(errorDataObject.error);
  };

  useEffect(() => {
    getAccessTokenHandler();
  }, [uploadUserId]);

  if (errorData) {
    return (
      <div className="text-red mt-2 flex flex-col gap-2">
        <p className="text-sm font-medium">{errorData.code}</p>
        <p className="text-sm">{errorData.error}</p>
        <Button
          type={ButtonType.TRY_AGAIN}
          version="v2"
          onClick={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <>
      <QuestionContentDisplay
        question="Verify identity to finish your order"
        prompt="Take a photo of your government-issued ID and a quick selfie. We’ll use them to confirm your identity before your prescription can be issued."
        className="!mb-0"
      />

      {isLoading ? (
        <LuLoader className="w-7 h-7 mx-auto mt-5 text-blue animate-spin" />
      ) : (
        <SumsubWebSdk
          accessToken={accessToken}
          onMessage={messageHandler}
          expirationHandler={() => {
            getAccessTokenHandler();
          }}
          config={config}
          options={options}
          onError={errorHandler}
        />
      )}
      {!hideSsnSwitch && (
        <SwitchStateLink
          label="Verify using the last 4 digits of your SSN instead"
          onClick={onUploadSsn}
        />
      )}
    </>
  );
};

export { SumsubState };
